/**
 * useStreamingChat — Hook für Token-by-Token SSE Chat-Streaming
 * mit Typing-Animation (zeichenweises Anzeigen) für flüssiges UX
 *
 * Anti-Flicker-Fix:
 * - Batched state updates: Tokens werden gesammelt und alle 50ms geflusht
 *   statt bei jedem einzelnen Zeichen (alle 18ms) ein setState auszulösen
 * - Reduziert Re-Renders von ~55/s auf ~20/s → kein sichtbares Flackern
 *
 * Unterstützt zwei Modi:
 * 1. Session-basiert: sessionId + message → /api/chat/stream (Server verwaltet History)
 * 2. Direkt: messages + clientId + chatType → /api/chat/stream (für Tagebuch-Coaching etc.)
 */
import { useState, useRef, useCallback, useEffect } from "react";

interface StreamMessage {
  role: string;
  content: string;
  imageUrl?: string;
}

interface StreamResult {
  success: boolean;
  content: string;
  sessionId: number | null;
}

interface UseStreamingChatOptions {
  onToken?: (token: string, fullText: string) => void;
  onDone?: (result: StreamResult) => void;
  onError?: (error: string) => void;
  /** Characters per tick for the typing animation (default: 3) */
  charsPerTick?: number;
  /** Milliseconds between ticks (default: 16) */
  tickInterval?: number;
  /** Milliseconds between state flushes to React (default: 50) */
  flushInterval?: number;
}

export function useStreamingChat(options: UseStreamingChatOptions = {}) {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const fullTextRef = useRef("");

  // Typing animation state
  const receivedTextRef = useRef(""); // Full text received from SSE so far
  const displayedLenRef = useRef(0); // How many chars have been "typed" to the UI
  const animFrameRef = useRef<number | null>(null);
  const streamDoneRef = useRef(false); // SSE stream finished

  // Batching: accumulate displayed text and flush to React at intervals
  const lastFlushedLenRef = useRef(0);
  const flushTimerRef = useRef<number | null>(null);

  const charsPerTick = options.charsPerTick ?? 3;
  const tickMs = options.tickInterval ?? 16;
  const flushMs = options.flushInterval ?? 50;

  // Store options in a ref to avoid stale closures
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current !== null) {
        clearTimeout(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Flush the current displayed text to React state.
   * Only fires onToken if the displayed length has actually changed
   * since the last flush — this prevents unnecessary re-renders.
   */
  const flushToReact = useCallback(() => {
    const currentLen = displayedLenRef.current;
    if (currentLen === lastFlushedLenRef.current) return;

    lastFlushedLenRef.current = currentLen;
    const visibleText = receivedTextRef.current.slice(0, currentLen);
    fullTextRef.current = visibleText;
    optionsRef.current.onToken?.("", visibleText);
  }, []);

  /**
   * Schedule a flush if one isn't already pending.
   */
  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current !== null) return;
    flushTimerRef.current = window.setTimeout(() => {
      flushTimerRef.current = null;
      flushToReact();
    }, flushMs) as unknown as number;
  }, [flushMs, flushToReact]);

  /**
   * Typing animation loop:
   * Reveals characters from receivedTextRef one-by-one (or charsPerTick at a time)
   * until displayedLen catches up with receivedText.length.
   * Does NOT call setState directly — instead schedules batched flushes.
   */
  const startTypingAnimation = useCallback(() => {
    if (animFrameRef.current !== null) return; // Already running

    const tick = () => {
      const received = receivedTextRef.current;
      const currentLen = displayedLenRef.current;

      if (currentLen < received.length) {
        // Reveal next chunk of characters
        const nextLen = Math.min(currentLen + charsPerTick, received.length);
        displayedLenRef.current = nextLen;

        // Schedule a batched flush instead of immediate setState
        scheduleFlush();

        // Schedule next tick
        animFrameRef.current = window.setTimeout(tick, tickMs) as unknown as number;
      } else if (streamDoneRef.current) {
        // Stream is done and we've displayed everything — do a final flush
        flushToReact();
        animFrameRef.current = null;
      } else {
        // Waiting for more data — check again soon
        animFrameRef.current = window.setTimeout(tick, tickMs) as unknown as number;
      }
    };

    animFrameRef.current = window.setTimeout(tick, tickMs) as unknown as number;
  }, [charsPerTick, tickMs, scheduleFlush, flushToReact]);

  /**
   * Session-basiertes Streaming: sessionId + message
   * Der Server verwaltet die History und baut den Kontext selbst.
   */
  const sendStreamingSession = useCallback(
    async (params: {
      sessionId: number;
      message: string;
    }): Promise<StreamResult> => {
      return _doStream("/api/chat/stream", params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTypingAnimation]
  );

  /**
   * Direktes Streaming: messages + clientId + chatType
   * Für Fälle wo der Client den Kontext selbst aufbaut (Tagebuch-Coaching etc.)
   */
  const sendStreaming = useCallback(
    async (params: {
      messages: StreamMessage[];
      clientId: string;
      chatType: "tagebuch" | "ayu" | "voice";
      sessionType?: string;
    }): Promise<StreamResult> => {
      return _doStream("/api/chat/stream-direct", params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTypingAnimation]
  );

  /**
   * Internes Streaming — gemeinsame Logik für beide Modi.
   * Der Server sendet entweder:
   *   - {content: "..."} für Tokens (chatRoutes.ts)
   *   - [DONE] als Abschluss
   * Oder (stream-direct):
   *   - {type: "token", token: "..."} für Tokens
   *   - {type: "done", fullContent: "...", sessionId: N}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _doStream = async (url: string, body: any): Promise<StreamResult> => {
    // Abort any existing stream
    if (abortRef.current) {
      abortRef.current.abort();
    }
    if (animFrameRef.current !== null) {
      clearTimeout(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    fullTextRef.current = "";
    receivedTextRef.current = "";
    displayedLenRef.current = 0;
    lastFlushedLenRef.current = 0;
    streamDoneRef.current = false;
    setIsStreaming(true);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      // Start the typing animation immediately
      startTypingAnimation();

      const decoder = new TextDecoder();
      let buffer = "";
      let result: StreamResult = { success: false, content: "", sessionId: null };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;

          const rawData = trimmed.slice(6);

          // Handle [DONE] marker (from chatRoutes.ts)
          if (rawData === "[DONE]") {
            result = {
              success: true,
              content: receivedTextRef.current,
              sessionId: null,
            };
            continue;
          }

          try {
            const data = JSON.parse(rawData);

            // Format 1: {content: "..."} from chatRoutes.ts
            if (data.content && typeof data.content === "string") {
              receivedTextRef.current += data.content;
            }
            // Format 2: {type: "token", token: "..."} from stream-direct
            else if (data.type === "token" && data.token) {
              receivedTextRef.current += data.token;
            }
            // Format 3: {type: "done", fullContent: "...", sessionId: N}
            else if (data.type === "done") {
              result = {
                success: true,
                content: data.fullContent || receivedTextRef.current,
                sessionId: data.sessionId ?? null,
              };
            }
            // Error
            else if (data.type === "error" || data.error) {
              const errMsg = data.message || data.error || "Unbekannter Fehler";
              result = {
                success: false,
                content: errMsg,
                sessionId: null,
              };
              optionsRef.current.onError?.(errMsg);
            }
          } catch {
            // Skip malformed JSON (heartbeat comments etc.)
          }
        }
      }

      // Mark stream as done — the animation will finish revealing remaining chars
      streamDoneRef.current = true;

      // Wait for the typing animation to finish displaying all received text
      await new Promise<void>((resolve) => {
        const waitForAnimation = () => {
          if (displayedLenRef.current >= receivedTextRef.current.length) {
            resolve();
          } else {
            setTimeout(waitForAnimation, 20);
          }
        };
        waitForAnimation();
      });

      // Cancel any pending flush timer
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      // Ensure final text is fully set with one last flush
      fullTextRef.current = receivedTextRef.current;
      lastFlushedLenRef.current = receivedTextRef.current.length;
      optionsRef.current.onToken?.("", receivedTextRef.current);

      if (result.success) {
        optionsRef.current.onDone?.(result);
      }

      setIsStreaming(false);
      abortRef.current = null;

      // Clean up animation
      if (animFrameRef.current !== null) {
        clearTimeout(animFrameRef.current);
        animFrameRef.current = null;
      }

      return result;
    } catch (error: unknown) {
      streamDoneRef.current = true;
      setIsStreaming(false);
      abortRef.current = null;

      if (animFrameRef.current !== null) {
        clearTimeout(animFrameRef.current);
        animFrameRef.current = null;
      }
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      if (error instanceof Error && error.name === "AbortError") {
        return { success: false, content: "", sessionId: null };
      }

      const errorMsg =
        "Es tut mir leid, es gab ein technisches Problem. Bitte versuche es erneut.";
      optionsRef.current.onError?.(errorMsg);
      return { success: false, content: errorMsg, sessionId: null };
    }
  };

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    streamDoneRef.current = true;
    if (animFrameRef.current !== null) {
      clearTimeout(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  return { sendStreaming, sendStreamingSession, isStreaming, abort, fullText: fullTextRef };
}
